// MCP add-on auto-wiring (stage 2) — wire an installed add-on's declared MCP server into the
// instance's per-agent MCP host configs automatically, ONCE, on `vortex update`.
//
// The contract (so base never needs to KNOW its add-ons): an add-on declares its server in its
// own package.json under `vortex.mcpServers` — the shape an MCP host's `.mcp.json` uses:
//   "vortex": { "mcpServers": { "vortex-computer-use": { "command": "node", "args": ["node_modules/@vortex-os/computer-use/scripts/mcp-stdio.mjs"] } } }
// base reads that from every installed `@vortex-os/*` add-on and merges into each agent's config.
// base hardcodes NOTHING about any add-on.
//
// TARGETS — every agent this instance routes for keeps a PROJECT-LOCAL MCP host config, so wiring
// is non-invasive (the user's GLOBAL agent config is never touched):
//   - Claude Code → `.mcp.json`            (JSON, `mcpServers`)
//   - Cursor      → `.cursor/mcp.json`     (JSON, `mcpServers`)
//   - Gemini CLI  → `.gemini/settings.json`(JSON, `mcpServers`)
//   - Codex CLI   → `.codex/config.toml`   (TOML, `[mcp_servers.<key>]` tables)
// The three JSON hosts share `.mcp.json`'s exact shape, so one pure planner serves all three; Codex
// is TOML and is handled by a parallel append-only planner (we add new tables, never rewrite the
// file — so a hand-maintained `config.toml` is preserved by construction, with no TOML dependency).
//
// Because what gets wired is EXECUTED by the agent host, the wiring is deliberately NARROW — an
// add-on may only auto-wire "run MY OWN script with node":
//   - `command` MUST be exactly `node` (no arbitrary executables — a compromised package can't
//     wire `curl|sh`; installing the package already trusts its postinstall, but auto-wiring must
//     not WIDEN that into a persistent host-executed command);
//   - `args[0]` MUST be a relative path UNDER `node_modules/@vortex-os/<declaring-addon>/` (the
//     add-on can only point at its own shipped scripts), no `..` segment, not absolute;
//   - no other arg may be absolute or contain a `..` segment; `env` is dropped (never wired).
// Anything outside this shape is REFUSED (reported as skippedUnsafe) — the user can still register
// it by hand. This keeps "install + update wires it" safe by construction.
//
// Other safety rules:
//  - ONE-SHOT, PER TARGET, like the statusline offer: a key is wired into a given agent's config at
//    most once (state file `data/.vortex/mcp-autowire.json`, keyed by target). User removal from one
//    agent is a deliberate opt-out FOR THAT AGENT, never refilled — and never affects another agent.
//  - ADD-ONLY: a key already present in a target's config is left exactly as-is (never clobbered).
//  - RESERVED-NAME GUARD: keys whose `trim().toLowerCase()` is host-reserved (`computer-use`) are
//    refused; keys must match a strict charset and never be `__proto__`/`constructor`/`prototype`.
//  - Each config is the USER's project config — written but NEVER auto-committed (caller reports it).
//  - A malformed/unreadable target config (or a non-object `mcpServers`) is REPORTED for that target,
//    never clobbered, and never blocks the other targets.

import { existsSync, readdirSync, readFileSync } from "node:fs";
import { mkdir } from "node:fs/promises";
import { dirname, join } from "node:path";
import { atomicWriteFile, type ModuleContext } from "@vortex-os/core";

/** MCP server keys the agent host reserves — writing them is a silent no-load, so we refuse (normalized compare). */
export const RESERVED_MCP_KEYS: ReadonlySet<string> = new Set(["computer-use"]);
/** Object keys that would pollute a prototype if used as a server key — never accepted. */
const PROTO_KEYS: ReadonlySet<string> = new Set(["__proto__", "constructor", "prototype"]);
/** A legitimate MCP server key: starts alphanumeric, then alphanumerics / dot / dash / underscore. */
const KEY_RE = /^[a-z0-9][a-z0-9._-]{0,63}$/i;

const STATE_SCHEMA = "vortex-mcp-autowire/2";
/** Legacy single-target state — a flat `wiredKeys` list that meant ".mcp.json" (Claude). Migrated on read. */
const STATE_SCHEMA_V1 = "vortex-mcp-autowire/1";

/** The agents this instance wires MCP servers for. `id` keys the one-shot state; `relPath` is repo-relative. */
export type WireTargetId = "claude" | "cursor" | "gemini" | "codex";
export interface WireTarget {
  readonly id: WireTargetId;
  readonly label: string;
  readonly relPath: string;
  readonly format: "json" | "toml";
}
export const WIRE_TARGETS: readonly WireTarget[] = [
  { id: "claude", label: "Claude Code", relPath: ".mcp.json", format: "json" },
  { id: "cursor", label: "Cursor", relPath: join(".cursor", "mcp.json"), format: "json" },
  { id: "gemini", label: "Gemini CLI", relPath: join(".gemini", "settings.json"), format: "json" },
  { id: "codex", label: "Codex CLI", relPath: join(".codex", "config.toml"), format: "toml" },
];

export interface DeclaredMcpServer {
  readonly command: string;
  readonly args: readonly string[];
  readonly env?: Readonly<Record<string, string>>;
}

export interface AddonMcpDeclaration {
  readonly addon: string; // package dir name under @vortex-os (e.g. "computer-use")
  readonly key: string; // server key as declared (e.g. "vortex-computer-use")
  readonly entry: DeclaredMcpServer;
}

/** A `.mcp.json`-shaped config (only the part we touch). */
export type McpConfig = { mcpServers?: Record<string, DeclaredMcpServer>; [k: string]: unknown };

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** Basic shape check for a declared entry — command:string + args:string[] (env optional string map). */
function validShape(v: unknown): v is DeclaredMcpServer {
  if (!isObj(v)) return false;
  if (typeof v.command !== "string") return false;
  if (!Array.isArray(v.args) || !v.args.every((a) => typeof a === "string")) return false;
  if (v.env !== undefined && (!isObj(v.env) || !Object.values(v.env).every((x) => typeof x === "string"))) return false;
  return true;
}

/** A server KEY we are willing to write: strict charset, not a prototype name, not host-reserved. */
export function isSafeServerKey(key: string): boolean {
  return typeof key === "string" && KEY_RE.test(key) && !PROTO_KEYS.has(key) && !RESERVED_MCP_KEYS.has(key.trim().toLowerCase());
}

function hasUnsafeSegment(p: string): boolean {
  const n = p.replace(/\\/g, "/");
  if (n.startsWith("/") || /^[a-zA-Z]:/.test(n)) return true; // absolute (posix or windows drive)
  return n.split("/").some((seg) => seg === "..");
}

/**
 * Constrain a declared entry to the safe shape: `node <relative script under the DECLARING add-on's
 * own dir>`, no `..`/absolute args, env dropped. Returns the narrowed entry, or null to REFUSE.
 */
export function sanitizeAddonEntry(addon: string, entry: DeclaredMcpServer): DeclaredMcpServer | null {
  if (!validShape(entry)) return null;
  if (entry.command !== "node") return null;
  if (entry.args.length === 0) return null;
  // A script-invocation arg is never a legitimate carrier of control characters; rejecting them is
  // defense-in-depth (and keeps the TOML emitter from ever having to encode a raw newline/NUL/DEL).
  if (entry.args.some((a) => Array.from(a).some((ch) => { const c = ch.charCodeAt(0); return c < 0x20 || c === 0x7f; }))) return null;
  const prefix = `node_modules/@vortex-os/${addon}/`;
  const first = entry.args[0].replace(/\\/g, "/");
  if (!first.startsWith(prefix)) return null;
  if (entry.args.some((a) => hasUnsafeSegment(a))) return null;
  return { command: "node", args: [...entry.args] }; // env deliberately dropped
}

/**
 * Read `vortex.mcpServers` declarations from every installed `@vortex-os/*` add-on (skip
 * `base`/`core`). Pure read; never throws (a bad package.json is skipped). Deterministic order.
 */
export function readAddonMcpDeclarations(repoRoot: string): AddonMcpDeclaration[] {
  const scope = join(repoRoot, "node_modules", "@vortex-os");
  let names: string[];
  try {
    names = (readdirSync(scope, { withFileTypes: true }) as import("node:fs").Dirent[])
      .filter((e) => (e.isDirectory() || e.isSymbolicLink()) && e.name !== "base" && e.name !== "core")
      .map((e) => e.name);
  } catch {
    return [];
  }
  const out: AddonMcpDeclaration[] = [];
  for (const addon of [...new Set(names)].sort()) {
    try {
      const pkg = JSON.parse(readFileSync(join(scope, addon, "package.json"), "utf8")) as { vortex?: { mcpServers?: unknown } };
      const decl = pkg?.vortex?.mcpServers;
      if (!isObj(decl)) continue;
      for (const key of Object.keys(decl).sort()) {
        const entry = (decl as Record<string, unknown>)[key];
        if (validShape(entry)) out.push({ addon, key, entry });
      }
    } catch {
      // unreadable / non-JSON package.json — skip this add-on
    }
  }
  return out;
}

export interface McpWirePlan {
  readonly config: McpConfig;
  readonly added: readonly { addon: string; key: string }[];
  /** Refused: reserved/invalid key. */
  readonly skippedReserved: readonly { addon: string; key: string }[];
  /** Refused: entry is not the safe `node <own script>` shape (possible injection). */
  readonly skippedUnsafe: readonly { addon: string; key: string }[];
  /** Key already in the config — left as-is. */
  readonly skippedPresent: readonly { addon: string; key: string }[];
  /** Key already wired once before (in state) — not re-added even if since removed. */
  readonly skippedAlreadyWired: readonly { addon: string; key: string }[];
  readonly newlyWiredKeys: readonly string[];
  readonly changed: boolean;
}

/**
 * PURE merge planner for a JSON MCP host config (`mcpServers` map). Decides what to add given
 * declarations, the existing config, and the keys already wired once into THIS target. Does not
 * mutate inputs. Servers map is null-prototype so a `__proto__` key in either side can never
 * pollute. The caller has already established that `existing.mcpServers` (if present) is a plain
 * object — see `wireAddonMcpServers`.
 */
export function planMcpWiring(
  declarations: readonly AddonMcpDeclaration[],
  existing: McpConfig | null,
  wiredBefore: ReadonlySet<string>,
): McpWirePlan {
  const base: McpConfig = isObj(existing) ? existing : {};
  const existingServers = isObj(base.mcpServers) ? (base.mcpServers as Record<string, DeclaredMcpServer>) : {};
  const servers: Record<string, DeclaredMcpServer> = Object.assign(Object.create(null), existingServers);
  const added: { addon: string; key: string }[] = [];
  const skippedReserved: { addon: string; key: string }[] = [];
  const skippedUnsafe: { addon: string; key: string }[] = [];
  const skippedPresent: { addon: string; key: string }[] = [];
  const skippedAlreadyWired: { addon: string; key: string }[] = [];

  for (const d of declarations) {
    const tag = { addon: d.addon, key: d.key };
    if (!isSafeServerKey(d.key)) { skippedReserved.push(tag); continue; }
    if (Object.prototype.hasOwnProperty.call(servers, d.key)) { skippedPresent.push(tag); continue; }
    if (wiredBefore.has(d.key)) { skippedAlreadyWired.push(tag); continue; }
    const safe = sanitizeAddonEntry(d.addon, d.entry);
    if (!safe) { skippedUnsafe.push(tag); continue; }
    servers[d.key] = safe;
    added.push(tag);
  }

  const changed = added.length > 0;
  // Re-materialize as a plain object for JSON output (null-proto stringifies fine, but a plain
  // object is what every reader expects); key insertion order is preserved.
  const config: McpConfig = changed ? { ...base, mcpServers: { ...servers } } : base;
  return {
    config,
    added,
    skippedReserved,
    skippedUnsafe,
    skippedPresent,
    skippedAlreadyWired,
    newlyWiredKeys: added.map((a) => a.key),
    changed,
  };
}

// ── Codex TOML (`.codex/config.toml`) ─────────────────────────────────────────────────────────
// Codex reads project-local `mcp_servers` from `.codex/config.toml`. We never PARSE/REWRITE the
// file (no TOML dependency, and a hand-maintained config must survive verbatim) — we only DETECT
// whether a server table is already present, and APPEND a new `[mcp_servers.<key>]` table when not.

/** A TOML bare key needs no quoting: `A-Za-z0-9_-`. Anything else (e.g. a dotted key) must be quoted. */
const TOML_BARE_KEY_RE = /^[A-Za-z0-9_-]+$/;

/** TOML basic-string escapes for the control characters that have a short form (code → escape text). */
const TOML_CTRL_ESCAPES: Readonly<Record<number, string>> = { 0x08: "\\b", 0x09: "\\t", 0x0a: "\\n", 0x0c: "\\f", 0x0d: "\\r" };

/**
 * Emit a TOML basic-string. A TOML basic-string may not contain a raw control character, so escape
 * backslash, double-quote, the short-form controls, and any remaining U+0000–U+001F / U+007F (DEL)
 * as `\uXXXX`. (`sanitizeAddonEntry` already rejects control chars, so this is belt-and-suspenders.)
 */
function tomlString(s: string): string {
  let out = "";
  for (const ch of s) {
    const c = ch.codePointAt(0) ?? 0;
    if (ch === "\\") out += "\\\\";
    else if (ch === '"') out += '\\"';
    else if (c < 0x20 || c === 0x7f) out += TOML_CTRL_ESCAPES[c] ?? "\\u" + c.toString(16).padStart(4, "0");
    else out += ch;
  }
  return `"${out}"`;
}

/** A TOML table-key segment: bare when safe, otherwise a quoted key (so `a.b` nests as one key, not two). */
function tomlKeySegment(key: string): string {
  return TOML_BARE_KEY_RE.test(key) ? key : tomlString(key);
}

/** Render one `[mcp_servers.<key>]` table block (trailing newline). Entry is already sanitized. */
function renderCodexServerBlock(key: string, entry: DeclaredMcpServer): string {
  const args = entry.args.map(tomlString).join(", ");
  return `[mcp_servers.${tomlKeySegment(key)}]\ncommand = ${tomlString(entry.command)}\nargs = [${args}]\n`;
}

/** Escape a string for safe literal inclusion in a RegExp. */
function reEscape(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Does the TOML text already declare a `mcp_servers` entry for `key`? Without a TOML parser we detect
 * every spelling that would COLLIDE with the `[mcp_servers.<key>]` table we append, so add-only holds
 * even before the one-shot state exists:
 *   (1) the dotted-table header `[mcp_servers.<key>]` — tolerant of inner whitespace and a trailing
 *       `# comment`; for a key that is NOT a TOML bare key (e.g. one containing a dot) ONLY the quoted
 *       form counts, since `[mcp_servers.a.b]` is a different (nested) structure than `[mcp_servers."a.b"]`;
 *   (2) a dotted top-level assignment `mcp_servers.<key> = …` (or `mcp_servers.<key>.sub = …`);
 *   (3) an inline `<key> = …` line inside an `[mcp_servers]` table block.
 * The one-shot per-target state is still the authoritative "already wired" guard on normal runs; this
 * presence scan is the safety net that prevents emitting a duplicate key into a hand-authored config.
 */
function codexHasServer(text: string, key: string): boolean {
  if (!text) return false;
  const k = reEscape(key);
  const bareSafe = TOML_BARE_KEY_RE.test(key);
  // A key only matches its OWN spelling: bare keys match bare-or-quoted; non-bare keys match quoted only.
  const keyForm = bareSafe ? `(?:${k}|"${k}"|'${k}')` : `(?:"${k}"|'${k}')`;

  // (1) dotted-table header, optional trailing comment
  if (new RegExp(`^[ \\t]*\\[[ \\t]*mcp_servers[ \\t]*\\.[ \\t]*${keyForm}[ \\t]*\\][ \\t]*(?:#.*)?$`, "m").test(text)) return true;
  // (2) dotted top-level assignment: mcp_servers.<key> = …  or  mcp_servers.<key>.sub = …
  if (new RegExp(`^[ \\t]*mcp_servers[ \\t]*\\.[ \\t]*${keyForm}[ \\t]*[.=]`, "m").test(text)) return true;
  // (3) inline assignment inside an [mcp_servers] table block (from its header to the next table header)
  const block = /^[ \t]*\[[ \t]*mcp_servers[ \t]*\][ \t]*(?:#.*)?$/m.exec(text);
  if (block) {
    const rest = text.slice(block.index + block[0].length);
    const next = /^[ \t]*\[/m.exec(rest);
    const body = next ? rest.slice(0, next.index) : rest;
    if (new RegExp(`^[ \\t]*${keyForm}[ \\t]*=`, "m").test(body)) return true;
  }
  return false;
}

export interface CodexWirePlan {
  /** Text to APPEND to the existing file ("" when nothing is added). */
  readonly appendText: string;
  readonly added: readonly { addon: string; key: string }[];
  readonly skippedReserved: readonly { addon: string; key: string }[];
  readonly skippedUnsafe: readonly { addon: string; key: string }[];
  readonly skippedPresent: readonly { addon: string; key: string }[];
  readonly skippedAlreadyWired: readonly { addon: string; key: string }[];
  readonly newlyWiredKeys: readonly string[];
  readonly changed: boolean;
}

/**
 * PURE append planner for Codex's TOML config. Mirrors `planMcpWiring`'s classification exactly
 * (reserved → present → already-wired → unsafe → add) but produces TOML text to append rather than a
 * merged object. `existingText` is the current file contents (null/"" when absent).
 */
export function planCodexWiring(
  declarations: readonly AddonMcpDeclaration[],
  existingText: string | null,
  wiredBefore: ReadonlySet<string>,
): CodexWirePlan {
  const text = existingText ?? "";
  const added: { addon: string; key: string }[] = [];
  const skippedReserved: { addon: string; key: string }[] = [];
  const skippedUnsafe: { addon: string; key: string }[] = [];
  const skippedPresent: { addon: string; key: string }[] = [];
  const skippedAlreadyWired: { addon: string; key: string }[] = [];
  const blocks: string[] = [];
  // Keys already staged for append THIS run. Unlike the JSON planner (which mutates its `servers` map
  // and so sees prior adds via `hasOwnProperty`), the TOML scan only sees the original file text — so
  // a second add-on declaring the same key would append a duplicate table. Track staged keys to skip it.
  const staged = new Set<string>();

  for (const d of declarations) {
    const tag = { addon: d.addon, key: d.key };
    if (!isSafeServerKey(d.key)) { skippedReserved.push(tag); continue; }
    if (codexHasServer(text, d.key) || staged.has(d.key)) { skippedPresent.push(tag); continue; }
    if (wiredBefore.has(d.key)) { skippedAlreadyWired.push(tag); continue; }
    const safe = sanitizeAddonEntry(d.addon, d.entry);
    if (!safe) { skippedUnsafe.push(tag); continue; }
    blocks.push(renderCodexServerBlock(d.key, safe));
    staged.add(d.key);
    added.push(tag);
  }

  return {
    appendText: blocks.join("\n"), // blank line between adjacent tables; each block ends in "\n"
    added,
    skippedReserved,
    skippedUnsafe,
    skippedPresent,
    skippedAlreadyWired,
    newlyWiredKeys: added.map((a) => a.key),
    changed: added.length > 0,
  };
}

/**
 * Append `appendText` to the existing TOML, separated by exactly one blank line. The existing content
 * is preserved VERBATIM (true append-only — we never trim or reflow a hand-maintained file); only the
 * separator is normalized so the new table starts on its own line after one blank line.
 */
function appendCodexText(existingText: string | null, appendText: string): string {
  const base = existingText ?? "";
  if (base.length === 0) return appendText;
  const sep = base.endsWith("\n\n") ? "" : base.endsWith("\n") ? "\n" : "\n\n";
  return `${base}${sep}${appendText}`;
}

// ── one-shot state (per target) ───────────────────────────────────────────────────────────────

export function mcpAutowireStatePath(ctx: ModuleContext): string {
  return join(ctx.dataDir, ".vortex", "mcp-autowire.json");
}

/** Per-target set of keys ever wired (so a later removal stays opted-out, per agent). */
type WiredState = Map<WireTargetId, Set<string>>;

function readWiredState(ctx: ModuleContext): WiredState {
  const state: WiredState = new Map();
  try {
    const raw = JSON.parse(readFileSync(mcpAutowireStatePath(ctx), "utf8")) as {
      schema?: string;
      targets?: Record<string, unknown>;
      wiredKeys?: unknown;
    };
    if (raw && raw.schema === STATE_SCHEMA && isObj(raw.targets)) {
      for (const t of WIRE_TARGETS) {
        const keys = raw.targets[t.id];
        if (Array.isArray(keys)) state.set(t.id, new Set(keys.filter((k): k is string => typeof k === "string")));
      }
      return state;
    }
    // v1 → v2 migration: the flat `wiredKeys` list meant ".mcp.json" (Claude). Preserve that opt-out.
    if (raw && raw.schema === STATE_SCHEMA_V1 && Array.isArray(raw.wiredKeys)) {
      state.set("claude", new Set(raw.wiredKeys.filter((k): k is string => typeof k === "string")));
      return state;
    }
  } catch {
    // missing / malformed — treat as nothing wired yet
  }
  return state;
}

function serializeState(state: WiredState): string {
  const targets: Record<string, string[]> = {};
  for (const t of WIRE_TARGETS) {
    const keys = state.get(t.id);
    if (keys && keys.size > 0) targets[t.id] = [...keys].sort();
  }
  return JSON.stringify({ schema: STATE_SCHEMA, targets }, null, 2) + "\n";
}

// ── public API ──────────────────────────────────────────────────────────────────────────────

/** Per-target outcome (surfaced inside `WireAddonMcpResult.perTarget`). */
export interface TargetWireResult {
  readonly target: WireTargetId;
  readonly label: string;
  readonly path: string;
  readonly status: "wired" | "nothing-to-wire" | "skipped-dry-run" | "malformed-config" | "write-failed";
  readonly added: readonly { addon: string; key: string }[];
}

export interface WireAddonMcpResult {
  readonly status: "wired" | "nothing-to-wire" | "skipped-dry-run" | "malformed-config" | "write-failed" | "no-addons";
  /** The primary (`.mcp.json`) path — kept for back-compat; per-target paths are in `perTarget`. */
  readonly path: string;
  /** Flattened, de-duplicated across targets. */
  readonly added: readonly { addon: string; key: string }[];
  readonly skippedReserved: readonly { addon: string; key: string }[];
  readonly skippedUnsafe: readonly { addon: string; key: string }[];
  readonly perTarget: readonly TargetWireResult[];
  readonly notes: readonly string[];
}

function dedupeTags(tags: readonly { addon: string; key: string }[]): { addon: string; key: string }[] {
  const seen = new Set<string>();
  const out: { addon: string; key: string }[] = [];
  for (const t of tags) {
    const k = `${t.addon}|${t.key}`;
    if (!seen.has(k)) { seen.add(k); out.push(t); }
  }
  return out;
}

/** What a single target needs in the write phase — computed purely in the plan phase. */
interface TargetPlan {
  readonly target: WireTarget;
  readonly absPath: string;
  readonly status: "wired" | "nothing-to-wire" | "malformed-config";
  readonly added: { addon: string; key: string }[];
  readonly skippedReserved: { addon: string; key: string }[];
  readonly skippedUnsafe: { addon: string; key: string }[];
  /** New file contents to write (null when nothing to write). */
  readonly configText: string | null;
  /** Next per-target wired set (null when unchanged — e.g. malformed). */
  readonly nextWired: Set<string> | null;
  readonly malformedNote?: string;
}

/** Parse + plan one JSON target. Never throws; a malformed config yields a `malformed-config` plan. */
function planJsonTarget(target: WireTarget, absPath: string, declarations: readonly AddonMcpDeclaration[], wiredBefore: ReadonlySet<string>): TargetPlan {
  let existing: McpConfig | null = null;
  if (existsSync(absPath)) {
    let text: string;
    try {
      text = readFileSync(absPath, "utf8").trim();
    } catch {
      return { target, absPath, status: "malformed-config", added: [], skippedReserved: [], skippedUnsafe: [], configText: null, nextWired: null, malformedNote: `${absPath} could not be read — left untouched.` };
    }
    if (text) {
      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch {
        return { target, absPath, status: "malformed-config", added: [], skippedReserved: [], skippedUnsafe: [], configText: null, nextWired: null, malformedNote: `${absPath} is not valid JSON — left untouched. Fix it, then re-run \`vortex update\`.` };
      }
      if (!isObj(parsed)) {
        return { target, absPath, status: "malformed-config", added: [], skippedReserved: [], skippedUnsafe: [], configText: null, nextWired: null, malformedNote: `${absPath} is not a JSON object — left untouched.` };
      }
      if (parsed.mcpServers !== undefined && !isObj(parsed.mcpServers)) {
        return { target, absPath, status: "malformed-config", added: [], skippedReserved: [], skippedUnsafe: [], configText: null, nextWired: null, malformedNote: `${absPath} has a non-object "mcpServers" — left untouched. Fix it, then re-run \`vortex update\`.` };
      }
      existing = parsed as McpConfig;
    }
  }
  const plan = planMcpWiring(declarations, existing, wiredBefore);
  const presentSafeKeys = plan.skippedPresent.map((s) => s.key);
  const nextWired = new Set([...wiredBefore, ...plan.newlyWiredKeys, ...presentSafeKeys]);
  return {
    target,
    absPath,
    status: plan.changed ? "wired" : "nothing-to-wire",
    added: [...plan.added],
    skippedReserved: [...plan.skippedReserved],
    skippedUnsafe: [...plan.skippedUnsafe],
    configText: plan.changed ? JSON.stringify(plan.config, null, 2) + "\n" : null,
    nextWired,
  };
}

/**
 * Read + plan the Codex TOML target. Never throws; an UNREADABLE file yields a `malformed-config`
 * plan. Note: unlike the JSON targets we cannot detect a *syntactically malformed* (but readable)
 * config.toml without a TOML parser (which we deliberately don't depend on) — but because we only
 * ever APPEND a new table (never parse/rewrite), wiring onto an already-broken file does not corrupt
 * it further, and `codexHasServer` still prevents emitting a duplicate of a key it can recognize.
 */
function planTomlTarget(target: WireTarget, absPath: string, declarations: readonly AddonMcpDeclaration[], wiredBefore: ReadonlySet<string>): TargetPlan {
  let existingText: string | null = null;
  if (existsSync(absPath)) {
    try {
      existingText = readFileSync(absPath, "utf8");
    } catch {
      return { target, absPath, status: "malformed-config", added: [], skippedReserved: [], skippedUnsafe: [], configText: null, nextWired: null, malformedNote: `${absPath} could not be read — left untouched.` };
    }
  }
  const plan = planCodexWiring(declarations, existingText, wiredBefore);
  const presentSafeKeys = plan.skippedPresent.map((s) => s.key);
  const nextWired = new Set([...wiredBefore, ...plan.newlyWiredKeys, ...presentSafeKeys]);
  return {
    target,
    absPath,
    status: plan.changed ? "wired" : "nothing-to-wire",
    added: [...plan.added],
    skippedReserved: [...plan.skippedReserved],
    skippedUnsafe: [...plan.skippedUnsafe],
    configText: plan.changed ? appendCodexText(existingText, plan.appendText) : null,
    nextWired,
  };
}

/**
 * Wire installed add-ons' declared MCP servers into every agent's project-local config (`.mcp.json`,
 * `.cursor/mcp.json`, `.gemini/settings.json`, `.codex/config.toml`), one-shot per target, add-only,
 * narrowly constrained (see header). Never throws — a malformed/unreadable target config is reported
 * and that target is left untouched without blocking the others.
 */
export async function wireAddonMcpServers(
  ctx: ModuleContext,
  options: { dryRun?: boolean } = {},
): Promise<WireAddonMcpResult> {
  const dryRun = options.dryRun ?? false;
  const primaryPath = join(ctx.repoRoot, ".mcp.json");
  const declarations = readAddonMcpDeclarations(ctx.repoRoot);
  if (declarations.length === 0) {
    return { status: "no-addons", path: primaryPath, added: [], skippedReserved: [], skippedUnsafe: [], perTarget: [], notes: [] };
  }

  const state = readWiredState(ctx);

  // ── plan phase (pure) ──
  const plans: TargetPlan[] = [];
  for (const target of WIRE_TARGETS) {
    const absPath = join(ctx.repoRoot, target.relPath);
    const wiredBefore = state.get(target.id) ?? new Set<string>();
    plans.push(target.format === "json" ? planJsonTarget(target, absPath, declarations, wiredBefore) : planTomlTarget(target, absPath, declarations, wiredBefore));
  }

  // ── notes (target-independent classifications first; per-target write outcomes are added below) ──
  const notes: string[] = [];
  const allReserved = dedupeTags(plans.flatMap((p) => p.skippedReserved));
  const allUnsafe = dedupeTags(plans.flatMap((p) => p.skippedUnsafe));
  for (const r of allReserved) notes.push(`Add-on "${r.addon}" declares an unusable/reserved MCP key "${r.key}" — refused (a reserved or malformed key silently won't load).`);
  for (const r of allUnsafe) notes.push(`Add-on "${r.addon}" MCP server "${r.key}" is not the safe \`node <own script>\` shape — refused (auto-wiring only runs an add-on's own bundled script). Register it by hand if you trust it.`);
  for (const p of plans) {
    if (p.malformedNote) notes.push(p.malformedNote);
  }

  // ── write phase ── CONFIG-FIRST, PER TARGET: write each target's config, then persist THAT target's
  // one-shot state immediately. This ordering gives both guarantees: a failed config write leaves its
  // target unadvanced (retried next update) and never opts another target out (the earlier
  // "state-first, all targets at once" let one failure permanently suppress the rest); and persisting
  // state right after each successful config write keeps the opt-out marker durable (a removed key is
  // not re-added on the next run). Removal is honored regardless of order anyway: a user-removed key is
  // `skippedAlreadyWired` in the PLAN (it sits in `wiredBefore`), so it is never re-added.
  const nextState: WiredState = new Map(state);
  const failedTargets = new Set<WireTargetId>();

  if (!dryRun) {
    for (const p of plans) {
      if (p.nextWired === null) continue; // malformed config — reported above, advance no state
      if (p.configText !== null) {
        try {
          await mkdir(dirname(p.absPath), { recursive: true });
          await atomicWriteFile(p.absPath, p.configText);
        } catch (e) {
          failedTargets.add(p.target.id);
          notes.push(`Could not write ${p.absPath} (${(e as Error).message}) — left untouched; will retry on the next \`vortex update\`.`);
          continue; // do not advance state for a target whose config did not land
        }
      }
      const before = state.get(p.target.id) ?? new Set<string>();
      if (p.nextWired.size !== before.size || [...p.nextWired].some((k) => !before.has(k))) {
        nextState.set(p.target.id, p.nextWired);
        try {
          await mkdir(join(ctx.dataDir, ".vortex"), { recursive: true });
          await atomicWriteFile(mcpAutowireStatePath(ctx), serializeState(nextState));
        } catch (e) {
          notes.push(`Could not persist MCP auto-wire state for ${p.target.label} (${(e as Error).message}); its server(s) were wired and will be recognized as present on the next update.`);
        }
      }
    }
  }

  // ── success note + aggregate result (a target whose write failed is NOT reported as wired) ──
  const wiredTargets = plans.filter((p) => p.status === "wired" && !failedTargets.has(p.target.id));
  if (wiredTargets.length > 0) {
    const detail = wiredTargets.map((p) => `${p.target.relPath} (${p.target.label}): ${p.added.map((a) => a.key).join(", ")}`).join("; ");
    notes.push(
      dryRun
        ? `Would wire add-on MCP server(s) — ${detail}.`
        : `Wired add-on MCP server(s) — ${detail}. Restart the agent (or approve the new server when prompted) to load the tools. (These are your project configs — review and commit them yourself.)`,
    );
  }

  const perTarget: TargetWireResult[] = plans.map((p) => ({
    target: p.target.id,
    label: p.target.label,
    path: p.absPath,
    status: failedTargets.has(p.target.id) ? "write-failed" : dryRun && p.status === "wired" ? "skipped-dry-run" : p.status,
    added: p.added,
  }));

  let status: WireAddonMcpResult["status"];
  if (wiredTargets.length > 0) status = dryRun ? "skipped-dry-run" : "wired";
  // A malformed target must SURFACE even when nothing was wired and the other targets were a clean
  // no-op — otherwise a non-object `.mcp.json` gets masked as "nothing-to-wire". Any malformed (with
  // no wiring) wins over nothing-to-wire; the per-target notes still name which config was bad.
  else if (plans.some((p) => p.status === "malformed-config")) status = "malformed-config";
  // Likewise, if wiring was planned but every write failed (no malformed, nothing landed), surface
  // "write-failed" rather than a clean "nothing-to-wire" — the failure notes name each bad path.
  else if (failedTargets.size > 0) status = "write-failed";
  else status = "nothing-to-wire";

  return {
    status,
    path: primaryPath,
    added: dedupeTags(wiredTargets.flatMap((p) => p.added)),
    skippedReserved: allReserved,
    skippedUnsafe: allUnsafe,
    perTarget,
    notes,
  };
}
